import { useCallback, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { useHttpApi } from './useHttpApi';
import {
  fetchValidateAddressWeb,
  IValidateAddressWebResponse200,
  IValidateAddressWebResponse400,
} from '../../../repositories/geo-suggest/v1/validate-address-web';
import { useFormTracking } from '../../../utils/useFormTracking';

export const VALIDATION_ERROR = 'Укажите дом';
export const SERVER_ERROR = 'Не удалось получить данные о доме, попробуй ввести другой адрес';

export interface IUseValidateAddress {
  onHouseIdChange(value: number | null): void;
}
export const useValidateAddress = ({ onHouseIdChange }: IUseValidateAddress) => {
  const httpApi = useHttpApi();

  const [isFetching, setIsFetching] = useState(false);
  const [validateError, setValidateError] = useState('');
  const { trackAddressFail } = useFormTracking();

  const resetValidation = useCallback(() => {
    onHouseIdChange(null);
    setValidateError('');
  }, [onHouseIdChange]);

  const validateAddress = useCallback(
    async (address: string) => {
      if (!httpApi) {
        return;
      }

      resetValidation();

      setIsFetching(true);

      try {
        const validateResponse = await fetchValidateAddressWeb<
          IValidateAddressWebResponse200,
          IValidateAddressWebResponse400
        >({
          httpApi,
          parameters: { address },
        });

        if (validateResponse.statusCode === 200) {
          onHouseIdChange(validateResponse.response.houseId);
        } else {
          trackAddressFail();

          setValidateError(VALIDATION_ERROR);
        }
      } catch (e) {
        setValidateError(SERVER_ERROR);
      } finally {
        setIsFetching(false);
      }
    },
    [httpApi, resetValidation, onHouseIdChange, trackAddressFail],
  );

  const validateAddressDebounced = useCallback(debounce(100, validateAddress), [validateAddress]);

  return {
    validateAddress: validateAddressDebounced,
    resetValidation,
    isFetching,
    validateError,
  };
};
